import React, { useState } from 'react';
import { Calendar, Clock, User, Phone, Mail, PawPrint } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import { timeSlots } from '../data/mockData';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const Appointment = () => {
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    petName: '',
    petType: '',
    ownerName: '',
    phone: '',
    email: '',
    time: '',
    notes: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !formData.time || !formData.petName || !formData.ownerName || !formData.phone) {
      toast.error('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    // Submit to backend API
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const appointmentData = {
      ...formData,
      date: format(date, 'dd MMMM yyyy', { locale: tr }),
    };

    try {
      const response = await fetch(`${BACKEND_URL}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointmentData),
      });

      if (!response.ok) throw new Error('Failed to create appointment');

      toast.success('Randevunuz başarıyla oluşturuldu! En kısa sürede sizi arayacağız.');
      
      // Reset form
      setFormData({
        petName: '',
        petType: '',
        ownerName: '',
        phone: '',
        email: '',
        time: '',
        notes: ''
      });
      setDate(undefined);
    } catch (error) {
      toast.error('Randevu oluşturulamadı. Lütfen tekrar deneyin.');
    }
  };

  return (
    <div className="min-h-screen">
      <Toaster position="top-center" />
      
      {/* Hero Section */}
      <section className="relative h-[300px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1770836037816-4445dbd449fd"
            alt="Randevu"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Randevu Al
          </h1>
          <p className="text-xl text-emerald-50">
            Evcil dostunuz için hızlı ve kolay randevu oluşturun
          </p>
        </div>
      </section>

      {/* Appointment Form */}
      <section className="py-20 bg-gradient-to-b from-white to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pet Name */}
                <div className="space-y-2">
                  <Label htmlFor="petName" className="text-gray-700 font-semibold flex items-center gap-2">
                    <PawPrint className="h-4 w-4 text-emerald-700" />
                    Evcil Hayvan Adı *
                  </Label>
                  <Input
                    id="petName"
                    name="petName"
                    value={formData.petName}
                    onChange={handleInputChange}
                    placeholder="Örn: Minnoş"
                    required
                    className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                {/* Pet Type */}
                <div className="space-y-2">
                  <Label htmlFor="petType" className="text-gray-700 font-semibold">
                    Hayvan Türü *
                  </Label>
                  <Select
                    value={formData.petType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, petType: value }))}
                    required
                  >
                    <SelectTrigger className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500">
                      <SelectValue placeholder="Seçiniz" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dog">Köpek</SelectItem>
                      <SelectItem value="cat">Kedi</SelectItem>
                      <SelectItem value="bird">Kuş</SelectItem>
                      <SelectItem value="other">Diğer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Owner Name */}
                <div className="space-y-2">
                  <Label htmlFor="ownerName" className="text-gray-700 font-semibold flex items-center gap-2">
                    <User className="h-4 w-4 text-emerald-700" />
                    Adınız Soyadınız *
                  </Label>
                  <Input
                    id="ownerName"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleInputChange}
                    placeholder="Adınız ve soyadınız"
                    required
                    className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-700 font-semibold flex items-center gap-2">
                    <Phone className="h-4 w-4 text-emerald-700" />
                    Telefon *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="0555 123 45 67"
                    required
                    className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-semibold flex items-center gap-2">
                    <Mail className="h-4 w-4 text-emerald-700" />
                    E-posta
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="ornek@email.com"
                    className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                  />
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <Label className="text-gray-700 font-semibold flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-emerald-700" />
                    Tarih *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal border-gray-300 hover:border-emerald-500"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {date ? format(date, 'dd MMMM yyyy', { locale: tr }) : 'Tarih seçin'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(date) => date < new Date() || date.getDay() === 0}
                        initialFocus
                        locale={tr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time */}
                <div className="space-y-2">
                  <Label htmlFor="time" className="text-gray-700 font-semibold flex items-center gap-2">
                    <Clock className="h-4 w-4 text-emerald-700" />
                    Saat *
                  </Label>
                  <Select
                    value={formData.time}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, time: value }))}
                    required
                  >
                    <SelectTrigger className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500">
                      <SelectValue placeholder="Saat seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((slot) => (
                        <SelectItem key={slot} value={slot}>
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-gray-700 font-semibold">
                  Notlar (Opsiyonel)
                </Label>
                <Textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Evcil hayvanınızın sağlık durumu veya özel notlarınız..."
                  rows={4}
                  className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-800 hover:to-emerald-700 text-white py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              >
                Randevu Oluştur
              </Button>

              <p className="text-sm text-gray-500 text-center">
                * işaretli alanlar zorunludur. Randevunuz onaylandıktan sonra sizi arayacağız.
              </p>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="h-8 w-8 text-emerald-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Telefon</h3>
              <p className="text-gray-600">+90 505 723 64 12</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-amber-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Çalışma Saatleri</h3>
              <p className="text-gray-600">Pazartesi - Cumartesi<br />09:00 - 19:00</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-emerald-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">E-posta</h3>
              <p className="text-gray-600">info@uzmanklinik.com</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Appointment;
